import { APIDetail, APIPolicy, APIPolicyConfiguration } from '../../../../remote-api/models'
import { APIPolicyBody } from '../../../../remote-api/APIManagerRemoteApi'
import { parseSemver } from '@mulesoft/monitoring-center-components/lib/components/SupportedRuntimesHelper/SupportedRuntimesHelper'
import MonitoringLocalStorageManager, { LogPointsLocalStorage } from '../../../../util/storage/MonitoringLocalStorageManager'
import { logPointsOrigin } from '../../LogPointsPage/model'
import { getFeatureFlag } from '../../../../util/flags'

export enum RuntimeMajor {
  MULE_THREE = 'mule-three',
  MULE_FOUR = 'mule-four',
  MULE_ONE = 'mule-one',
}

export type RuntimeSemver = {
  major: number
  minor: number
  patch: number
}

export type RuntimeSupport = {
  minMuleVersion: RuntimeSemver
}

const oneDayInMilliseconds = 86400000

export abstract class LogPointsRuntimeHelper {

  public static readonly POLICY_ID = 'message-logging'

  public static readonly GROUP_ID = getFeatureFlag('logPoints.apis.muleGroupId') || '68ef9520-24e9-4cf2-b2f5-620025690913'

  public readonly runtime: RuntimeMajor

  static getMajor(major: number) {
    switch (major) {
      case 4:
        return RuntimeMajor.MULE_FOUR

      case 3:
        return RuntimeMajor.MULE_THREE

      default:
        return RuntimeMajor.MULE_ONE
    }
  }

  protected constructor(runtime: RuntimeMajor) {
    this.runtime = runtime
  }

  async fetchRuntimeSupport(orgId: string): Promise<RuntimeSupport> {
    const minMuleVersionFromLocalStorage = this.fetchRuntimeSupportFromLocalStorage()
    if (minMuleVersionFromLocalStorage) return this.handleMinMuleVersion(minMuleVersionFromLocalStorage)

    const fetchedMinMuleVersion = await this.fetchMinMuleVersion(orgId)
    if (fetchedMinMuleVersion !== undefined) this.saveRuntimeSupportInLocalStorage(fetchedMinMuleVersion)

    return this.handleMinMuleVersion(fetchedMinMuleVersion)
  }

  abstract fetchMessageLoggingPolicy(orgId: string, envId: string, apiId: string): Promise<APIPolicy | undefined>

  abstract getAPIPolicyBody(api: APIDetail, configuration: APIPolicyConfiguration, policy?: APIPolicy): Promise<APIPolicyBody>

  abstract canHandle(api: APIDetail): boolean

  protected abstract getDefaultRuntimeSupport(): RuntimeSupport

  protected abstract fetchMinMuleVersion(orgId: string): Promise<string | undefined>

  private async handleMinMuleVersion(minMuleVersion?: string) {
    if (!minMuleVersion) {
      return this.getDefaultRuntimeSupport()
    }

    const semver = parseSemver(minMuleVersion)

    return semver ? {minMuleVersion: semver} : this.getDefaultRuntimeSupport()
  }

  private fetchRuntimeSupportFromLocalStorage() {
    const storage = MonitoringLocalStorageManager.get(logPointsOrigin)
    if (!storage) return undefined

    const runtimeSupport = (storage as LogPointsLocalStorage).runtimeSupport
    if (!runtimeSupport) return undefined

    const specificRuntimeSupport = runtimeSupport[this.runtime]
    if (!specificRuntimeSupport) return undefined

    const expiration = new Date(specificRuntimeSupport.expiration)
    const now = new Date()
    if (now > expiration) return undefined

    return specificRuntimeSupport.support
  }

  private saveRuntimeSupportInLocalStorage(fetchedMinMuleVersion: string) {
    const storage = MonitoringLocalStorageManager.get(logPointsOrigin)
    if (!storage) return

    const runtimeSupport = (storage as LogPointsLocalStorage).runtimeSupport
    const expiration = new Date().getTime() + oneDayInMilliseconds
    const thisRuntimeSupport = {[this.runtime]: {support: fetchedMinMuleVersion, expiration}}
    const newRuntimeSupport = runtimeSupport ? {...runtimeSupport, ...thisRuntimeSupport} : {...thisRuntimeSupport}
    const newStorage = {...storage, runtimeSupport: newRuntimeSupport}

    MonitoringLocalStorageManager.put(logPointsOrigin, newStorage)
  }
}
